import { ARCHETYPE_BY_ID, particleSource, type AuraFx } from "@nano/content";
import type { AvatarId, AvatarPose, Equipment } from "@nano/contracts";
import { useEffect, useRef, useState } from "react";
import { assetEntry, imageUrl } from "../../services/assets/assets.ts";
import { AURA_FEET_Y, auraGlow, auraParticles, STANDING, wearMotion } from "../../game/avatar/motion.ts";
import { cutParticle } from "../../game/avatar/particleArt.ts";
import { layoutWear, type Crop, type WearLayout, type WearPiece } from "../../game/avatar/rig.ts";
import { Art } from "./common.tsx";

const BODY_ORIGIN_Y = 0.96;

export const clip = (c: Crop) => `inset(${c.y0 * 100}% ${(1 - c.x1) * 100}% ${(1 - c.y1) * 100}% ${c.x0 * 100}%)`;
const hex = (color: number) => `#${color.toString(16).padStart(6, "0")}`;

/** Same transform order as Phaser: scale first, then rotate about the seat. */
export function pieceTransform(p: WearPiece, angleDeg = 0, sx = 1, sy = 1): string {
  return `rotate(${p.rotation + (angleDeg * Math.PI) / 180}rad) scale(${p.scaleX * sx}, ${sy})`;
}

/**
 * DOM avatar for menus (shop, wardrobe, picker). Uses the same rig and
 * motion code as the Phaser avatar so try-ons look identical in the city:
 * items are fitted to the body, wings flap, props sway and auras are live
 * particles.
 */
export function AvatarPreview({
  avatarId,
  equipped,
  height,
  pose = "front",
  bounce,
  sidePad = 0.3,
}: {
  avatarId: AvatarId;
  equipped: Equipment;
  height: number;
  pose?: AvatarPose;
  bounce?: boolean;
  /** Horizontal room for wings/auras, as a fraction of height. */
  sidePad?: number;
}) {
  const rigRef = useRef<HTMLDivElement>(null);
  const archetype = ARCHETYPE_BY_ID.get(avatarId);
  const bodyId = archetype?.bodyAssets[pose] ?? "";
  const bodyEntry = assetEntry(bodyId);
  const ratio = bodyEntry ? bodyEntry.width / bodyEntry.height : 0.52;
  const body = { width: height * ratio, height };
  const layout: WearLayout = archetype
    ? layoutWear(avatarId, pose, equipped, body, (assetId) => {
        const entry = assetEntry(assetId);
        return entry ? { width: entry.width, height: entry.height } : { width: 100, height: 100 };
      })
    : { pieces: [], arms: [], hands: [], aura: null };

  const particleUrls = useParticleArt(layout.aura?.itemId, layout.aura?.fx);
  useLiveWear(rigRef, layout, height, pose === "side", particleUrls);
  if (!archetype) return null;

  const renderPiece = (p: WearPiece, i: number) => (
    <span
      key={`${p.slot}-${i}`}
      className="avatar-preview__item"
      data-piece={p.motion ? i : undefined}
      style={{
        left: p.x - p.width * p.originX,
        top: p.y - p.height * p.originY,
        width: p.width,
        height: p.height,
        zIndex: p.layer === "back" ? 0 : 2,
        transformOrigin: `${p.originX * 100}% ${p.originY * 100}%`,
        transform: pieceTransform(p),
        clipPath: p.crop ? clip(p.crop) : undefined,
      }}
    >
      <Art id={p.assetId} />
    </span>
  );
  const aura = layout.aura;
  // Leave room above for hats and around for auras.
  const padTop = height * 0.18;
  const padX = height * sidePad;
  return (
    <div
      className={`avatar-preview ${bounce ? "avatar-preview--bounce" : ""}`}
      style={{ width: body.width + padX * 2, height: height * (1 + 0.18 + 1 - BODY_ORIGIN_Y) }}
      data-testid="avatar-preview"
    >
      <div ref={rigRef} className="avatar-preview__rig" style={{ left: padX, top: padTop, width: body.width, height: body.height }}>
        {aura && (
          <div className="avatar-preview__aura" style={{ left: aura.x, top: aura.y }}>
            <span
              className="avatar-preview__glow"
              data-glow
              style={{ top: AURA_FEET_Y * height, width: height * 0.62, height: height * 0.16, background: `radial-gradient(closest-side, ${hex(aura.fx.glow)}, transparent)` }}
            />
          </div>
        )}
        {aura &&
          particleUrls &&
          auraParticles(aura.fx, 0).map((_, i) => (
            <span key={`fx-${i}`} className="avatar-preview__particle" data-particle={i} style={{ left: aura.x, top: aura.y }} />
          ))}
        {layout.pieces.map((p, i) => (p.layer === "back" ? renderPiece(p, i) : null))}
        <Art id={bodyId} className="avatar-preview__body" />
        {layout.arms.map((a, i) => (
          <span
            key={`arm-${i}`}
            className="avatar-preview__arm"
            style={{
              left: a.x1,
              top: a.y1 - a.thickness / 2,
              width: Math.hypot(a.x2 - a.x1, a.y2 - a.y1),
              height: a.thickness,
              background: hex(a.color),
              transform: `rotate(${Math.atan2(a.y2 - a.y1, a.x2 - a.x1)}rad)`,
            }}
          />
        ))}
        {layout.pieces.map((p, i) => (p.layer === "front" ? renderPiece(p, i) : null))}
        {layout.hands.map((outline, i) => (
          <span
            key={`hand-${i}`}
            className="avatar-preview__patch"
            style={{ clipPath: `polygon(${outline.map(([x, y]) => `${x * 100}% ${y * 100}%`).join(", ")})` }}
          >
            <Art id={bodyId} className="avatar-preview__body" />
          </span>
        ))}
      </div>
    </div>
  );
}

/** Drives moving pieces and aura particles from one animation-frame clock. */
export function useLiveWear(
  rigRef: React.RefObject<HTMLDivElement | null>,
  layout: WearLayout,
  height: number,
  side: boolean,
  particleUrls: string[] | null,
) {
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const animated = layout.aura !== null || layout.pieces.some((p) => p.motion);
  useEffect(() => {
    const rig = rigRef.current;
    if (!rig || !animated) return;
    const still = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    const start = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      const t = still ? 1.2 : (now - start) / 1000;
      const current = layoutRef.current;
      rig.querySelectorAll<HTMLElement>("[data-piece]").forEach((el) => {
        const p = current.pieces[Number(el.dataset.piece)];
        if (!p) return;
        const m = wearMotion(p.motion, t, STANDING, side);
        el.style.transform = pieceTransform(p, m.angle, m.sx, m.sy);
      });
      const aura = current.aura;
      if (aura) {
        const glow = rig.querySelector<HTMLElement>("[data-glow]");
        const pulse = auraGlow(t * 1000);
        if (glow) {
          glow.style.opacity = String(0.45 + 0.35 * pulse);
          glow.style.transform = `translate(-50%, -50%) scale(${0.92 + 0.12 * pulse})`;
        }
        const particles = auraParticles(aura.fx, t * 1000);
        rig.querySelectorAll<HTMLElement>("[data-particle]").forEach((el) => {
          const p = particles[Number(el.dataset.particle)];
          if (!p) return;
          const f = aura.fx.frames[p.frame]!;
          const k = (p.size * height) / f.w;
          const url = `url(${particleUrls?.[p.frame] ?? ""})`;
          if (el.style.backgroundImage !== url) el.style.backgroundImage = url;
          el.style.width = `${f.w}px`;
          el.style.height = `${f.h}px`;
          el.style.transform = `translate(${p.x * height - f.w / 2}px, ${p.y * height - f.h / 2}px) rotate(${p.angle}deg) scale(${k})`;
          el.style.opacity = String(p.alpha);
          el.style.zIndex = p.front ? "3" : "0";
        });
      }
      if (!still) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [rigRef, animated, height, side, particleUrls]);
}

const particleCache = new Map<string, string[]>();

function loadImage(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = src;
  return image.decode().then(() => image);
}

/** Aura particles cut out of the painted aura art (see particleArt.ts), as image URLs. */
export function useParticleArt(auraId: string | undefined, fx: AuraFx | undefined): string[] | null {
  const [urls, setUrls] = useState<string[] | null>(() => (auraId ? (particleCache.get(auraId) ?? null) : null));
  useEffect(() => {
    if (!auraId || !fx) {
      setUrls(null);
      return;
    }
    const cached = particleCache.get(auraId);
    if (cached) {
      setUrls(cached);
      return;
    }
    let live = true;
    const sources = fx.frames.map((frame) => particleSource(auraId, frame));
    void Promise.all(sources.map(({ assetId }) => loadImage(imageUrl(assetId) ?? ""))).then(
      (images) => {
        const cut = fx.frames.map((frame, i) => cutParticle(images[i]!, frame, sources[i]!.cut).toDataURL());
        particleCache.set(auraId, cut);
        if (live) setUrls(cut);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [auraId, fx]);
  return urls;
}
